/**
 * Web Vitals Tracking
 * Monitors Core Web Vitals (LCP, FID, CLS, INP, TTFB) and reports to GA4
 */

type MetricName = 'CLS' | 'FCP' | 'FID' | 'INP' | 'LCP' | 'TTFB';

interface WebVitalMetric {
  name: MetricName;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  id: string;
}

// Thresholds based on Google's Core Web Vitals standards
const thresholds: Record<MetricName, [number, number]> = {
  CLS: [0.1, 0.25],
  FCP: [1800, 3000],
  FID: [100, 300],
  INP: [200, 500],
  LCP: [2500, 4000],
  TTFB: [800, 1800],
};

function getRating(name: MetricName, value: number): 'good' | 'needs-improvement' | 'poor' {
  const [good, poor] = thresholds[name];
  if (value <= good) return 'good';
  if (value <= poor) return 'needs-improvement';
  return 'poor';
}

/**
 * Report metric to Google Analytics 4
 */
function reportToGA4(metric: WebVitalMetric) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', metric.name, {
      event_category: 'Web Vitals',
      event_label: metric.id,
      value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
      metric_rating: metric.rating,
      metric_delta: Math.round(metric.delta),
      non_interaction: true,
    });
    
    console.log(`[Web Vitals] ${metric.name}: ${metric.value.toFixed(2)} (${metric.rating})`);
  }
}

/**
 * Observe Largest Contentful Paint (LCP)
 */
function observeLCP() {
  if (!('PerformanceObserver' in window)) return;
  
  try {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1] as PerformanceEntry & { startTime: number };
      
      if (lastEntry) {
        const value = lastEntry.startTime;
        reportToGA4({
          name: 'LCP',
          value,
          rating: getRating('LCP', value),
          delta: value,
          id: `lcp-${Date.now()}`,
        });
      }
    });
    
    observer.observe({ type: 'largest-contentful-paint', buffered: true });
  } catch (e) {
    // Silent fail for unsupported browsers
  }
}

/**
 * Observe First Input Delay (FID) / Interaction to Next Paint (INP)
 */
function observeInteraction() {
  if (!('PerformanceObserver' in window)) return;
  
  try {
    // FID
    const fidObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries() as (PerformanceEntry & { processingStart: number; startTime: number })[];
      
      for (const entry of entries) {
        if (entry.processingStart) {
          const value = entry.processingStart - entry.startTime;
          reportToGA4({
            name: 'FID',
            value,
            rating: getRating('FID', value),
            delta: value,
            id: `fid-${Date.now()}`,
          });
          break; // Only report first input
        }
      }
    });
    
    fidObserver.observe({ type: 'first-input', buffered: true });
    
    // INP (Interaction to Next Paint)
    let maxINP = 0;
    const inpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries() as (PerformanceEntry & { duration: number })[];
      
      for (const entry of entries) {
        if (entry.duration > maxINP) {
          maxINP = entry.duration;
        }
      }
    });
    
    inpObserver.observe({ type: 'event', buffered: true });
    
    // Report INP on page hide
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden' && maxINP > 0) {
        reportToGA4({
          name: 'INP',
          value: maxINP,
          rating: getRating('INP', maxINP),
          delta: maxINP,
          id: `inp-${Date.now()}`,
        });
      }
    });
  } catch (e) {
    // Silent fail for unsupported browsers
  }
}

/**
 * Observe Cumulative Layout Shift (CLS)
 */
function observeCLS() {
  if (!('PerformanceObserver' in window)) return;
  
  try {
    let clsValue = 0;
    let sessionValue = 0;
    let sessionEntries: PerformanceEntry[] = [];
    
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries() as (PerformanceEntry & { value: number; hadRecentInput: boolean })[];
      
      for (const entry of entries) {
        if (!entry.hadRecentInput) {
          const firstSessionEntry = sessionEntries[0] as (PerformanceEntry & { startTime: number }) | undefined;
          const lastSessionEntry = sessionEntries[sessionEntries.length - 1] as (PerformanceEntry & { startTime: number }) | undefined;
          
          if (
            sessionValue &&
            entry.startTime - (lastSessionEntry?.startTime || 0) < 1000 &&
            entry.startTime - (firstSessionEntry?.startTime || 0) < 5000
          ) {
            sessionValue += entry.value;
            sessionEntries.push(entry);
          } else {
            sessionValue = entry.value;
            sessionEntries = [entry];
          }
          
          if (sessionValue > clsValue) {
            clsValue = sessionValue;
          }
        }
      }
    });
    
    observer.observe({ type: 'layout-shift', buffered: true });
    
    // Report on page hide
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden' && clsValue > 0) {
        reportToGA4({
          name: 'CLS',
          value: clsValue,
          rating: getRating('CLS', clsValue),
          delta: clsValue,
          id: `cls-${Date.now()}`,
        });
      }
    });
  } catch (e) {
    // Silent fail for unsupported browsers
  }
}

/**
 * Observe Time to First Byte (TTFB) and First Contentful Paint (FCP)
 */
function observeNavigationTiming() {
  if (!('PerformanceObserver' in window)) return;
  
  try {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries() as (PerformanceNavigationTiming)[];
      
      for (const entry of entries) {
        // TTFB
        const ttfb = entry.responseStart - entry.requestStart;
        if (ttfb > 0) {
          reportToGA4({
            name: 'TTFB',
            value: ttfb,
            rating: getRating('TTFB', ttfb),
            delta: ttfb,
            id: `ttfb-${Date.now()}`,
          });
        }
      }
    });
    
    observer.observe({ type: 'navigation', buffered: true });
    
    // FCP
    const fcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const fcpEntry = entries.find(e => e.name === 'first-contentful-paint') as (PerformanceEntry & { startTime: number }) | undefined;
      
      if (fcpEntry) {
        reportToGA4({
          name: 'FCP',
          value: fcpEntry.startTime,
          rating: getRating('FCP', fcpEntry.startTime),
          delta: fcpEntry.startTime,
          id: `fcp-${Date.now()}`,
        });
      }
    });
    
    fcpObserver.observe({ type: 'paint', buffered: true });
  } catch (e) {
    // Silent fail for unsupported browsers
  }
}

/**
 * Initialize all Web Vitals observers
 * Call this once on app mount
 */
export function initWebVitals() {
  if (typeof window === 'undefined') return;
  
  // Defer to not block main thread
  if ('requestIdleCallback' in window) {
    (window as Window & { requestIdleCallback: (cb: () => void) => void }).requestIdleCallback(() => {
      observeLCP();
      observeInteraction();
      observeCLS();
      observeNavigationTiming();
    });
  } else {
    setTimeout(() => {
      observeLCP();
      observeInteraction();
      observeCLS();
      observeNavigationTiming();
    }, 0);
  }
  
  console.log('[Web Vitals] Monitoring initialized');
}
